// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : ui persistence (reducers + hooks)
// =============================================================================
// ---Third-party imports
import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
  createContext,
  useDebugValue,
} from 'react'
//~ import {map as MapStructure} from 'redux-data-structures'

import {useSelector, useDispatch} from 'react-redux'

// ---Framework imports
import {
  get,
  mapItems,
  filterItems,
  dict,
  keys,
  isNil,
  isString,
  values,
  isObject,
  has,
  isNumber,
  isBoolean,
} from 'ldash'
import log from 'uix/logging'
import {reduceMergeReducers, handleSubState, handleAction} from 'uix/utils/reducers'
import {usePartContext} from 'uix/parts/hooks'

import {useRouter, useEffectSSR, useResettableState} from 'uix/hooks'

// ---Local imports
import {uiSetValue, clearPartPersistence} from './actions'
import {CREATION_TRACKER} from './utils'
export {uiSetValue}

// store ui state under this primary store key
export const STATE_KEY = 'ui'

// version -1 is a custom test injection
const TEST_INJECT_VER = -1

const migrateUiStructure = ui => {
  if (values(ui.byId).some(e => isNil(e.version))) {
    log.warn('reject ui structure %O', ui.byId)
    // version 2: no allIds, and byId substructured into {value, version}
    return {byId: {}}
  }
  return ui
}

const reducers = [
  //~ handleAction('persist/REHYDRATE', ui => ({...ui, byId: {}, allIds: []})),
  handleAction('SET_APP_STATE', (ui, payload) => {
    if (payload.state == 'INIT_APP') {
      ui = migrateUiStructure(ui)
    }
    return ui
  }),
  handleAction('INIT_UI_STATE', ({byId, ...ui}, {payload}) => {
    // XXXX: disabled
    //~ return {...ui, byId}
    let loadedUiState = payload.uistate.ui
    let version_check = (key, data) =>
      !byId[key] || // no local data
      byId[key].version < data.version || // local data has old version
      (byId[key].version === data.version && byId[key].date < data.date) || // local data has same version but older timestamp
      data.version == TEST_INJECT_VER // custom remote data

    let loadedValues = dict(filterItems(loadedUiState, version_check))

    byId = {...byId, ...loadedValues}
    return migrateUiStructure({...ui, byId})
  }),
  handleAction(
    uiSetValue,
    ({byId, ...ui}, {meta, payload}) => {
      let old_data = byId[meta.name] || {}
      let {value, merge, version, initialValue} = payload

      // either shallow update or full replace (merge=false)
      let data = {...old_data, date: new Date().valueOf()}
      if (!isNil(version)) {
        // on create
        data = {...data, version}
      }
      if (initialValue !== undefined) {
        // on create
        data = {...data, initialValue}
      }

      // XXX: isObject is only a artefact for setting raw values via python test system
      if (merge && isObject(value)) {
        data = {
          ...data,
          value: value.__raw__ !== undefined ? value.__raw__ : {...old_data.value, ...value},
        }
      } else {
        data = {...data, value: value.__raw__ !== undefined ? value.__raw__ : value}
      }
      return {...ui, byId: {...byId, [meta.name]: data}}
    },
    {byId: {}}
  ),
  handleAction(clearPartPersistence, ({byId, ...ui}, {meta, payload}) => {
    let {partName} = payload
    byId = dict(
      mapItems(byId, (name, data) => {
        if (name.startsWith(`Part/${partName}/`) && data.initialValue !== undefined) {
          data = {...data, value: data.initialValue}
        }
        return [name, data]
      })
    )
    return {...ui, byId}
  }),
]

// wrapping components with PersistenceContext to
// add another subkey to redux persistence storage location
export const PersistenceContext = createContext(null)
export const usePersistenceContext = () => useContext(PersistenceContext)

export const uiReducer = reduceMergeReducers(...reducers)

export const versionCheck = (values, version, defaults, uiStoreKey, verbose = false) => {
  let v1 = version // stored version
  let v2 = defaults.__version__ ?? 0 // default version from "spec"
  if (v1 !== v2) {
    //~ log.debug('check ui props version %o %s->%s', uiStoreKey, v1, v2)
    if (v1 === TEST_INJECT_VER) {
      // merge test injection
      log.ssr(`injecting %o into %o`, values, uiStoreKey)
      return {...defaults, ...values, __version__: v2}
    }
    if (verbose && v1 !== undefined)
      log.warn(`resetting ui props of %o from version %o to %o`, uiStoreKey, v1, v2)
    return {...defaults, __version__: v2, __needCreate__: true}
  }
  return values
}

/* hooks */
const checkSimpleScalar = v => {
  if (!(isString(v) || isNumber(v) || v === null || isBoolean(v))) {
    log.error('only string+number+null support')
    return false
  }
  return true
}

export const usePersistentWithQueryOverride = ({
  name, // persistence store key
  queryName, // optional custom query param name
  replace = false, // replace=true -> on-setValue no browser history change
  initialValue = '',
  version = 0,
}) => {
  /* mirror persisted value into url query params
   */
  if (!checkSimpleScalar(initialValue)) {
    return [null, () => null]
  }
  queryName = queryName || name
  let {request, go, replaceQueryParams} = useRouter()
  // reset changed if query param changes
  let [changed, setChanged] = useResettableState(false, [request.query[queryName]])

  // store key: Part/NAME/query/...
  let [value, setValue] = usePersistent(`query/${queryName}`, initialValue, version)

  let setValueAndGo = useCallback(v => {
    if (!checkSimpleScalar(v)) return null
    setValue(v)
    if (!changed) setChanged(true)

    let query_v = isBoolean(v) || isNumber(v) ? v : v || null // null means query param deletion
    let updateQuery = {[name]: query_v}
    if (replace) {
      replaceQueryParams(updateQuery)
    } else {
      go({updateQuery})
    }
  })
  // prefer query param if not changed
  let res_value = useMemo(() => (changed ? value : request.query[queryName] ?? value), [
    request.query,
    value,
    changed,
  ])
  return [res_value, setValueAndGo]
}

export const usePersistent = (name, initialValue = null, version = 0) => {
  /* simpler (raw) version of usePersistentUI - returns only a [value, setter()] like React.useState()
  - also binds name to partName context (if name does not start with "Global/" )
  */
  let {ui, setUI} = usePersistentUI(name, {__raw__: initialValue, __version__: version})
  let res = useMemo(
    () => [
      ui.__raw__,
      raw => {
        if (raw === undefined) {
          // undefined will lead to endless update loops (null is a valid state and allowed)
          log.warn('persist %s: undefined is not allowed', name)
          return
        }
        if (raw !== ui.__raw__) {
          setUI({__raw__: raw})
        }
      },
    ],
    [ui, setUI]
  )
  useDebugValue(res, res => res[0])
  return res
}

export const usePersistentShallow = (name, defaults = {}, version = 0) => {
  /* like usePersistent but manages a shallow updated dict structure
  - also binds name to partName context (if name does not start with "Global/")
  usage:
    let [opts, setOpts] = usePersistentShallow(NAME, {opt1: 1, opt2: 2})
  set values:
    setOpts(key, value)
    setOpts({key1: value1, key2: value2})
   */
  let {__version__, ...data} = defaults // also accept __version__
  let {ui, setUI} = usePersistentUI(name, {...data, __version__: __version__ ?? version})
  let set = useCallback((p1, p2) => (isString(p1) || isNumber(p1) ? setUI({[p1]: p2}) : setUI(p1)))
  return [ui, set]
}

const usePersistentUI = (uiStoreKey, defaults = {}) => {
  /* like the older redux-connect() with 'uiDefaults' but now in hook form
  - binds to Part/partName/uiStoreKey (if uiStoreKey does not start with "Global/")
  - defaults will contain a __version__ param
   */
  let dispatch = useDispatch()
  let {name: partName} = usePartContext()
  let persistenceContext = usePersistenceContext()

  if (!isString(uiStoreKey)) {
    throw Error(`uiStoreKey needs to be a string - but is: ${uiStoreKey}`)
  }

  if (!uiStoreKey.startsWith('Global/')) {
    let ctx = persistenceContext ? `${partName}/${persistenceContext}` : partName
    uiStoreKey = `Part/${ctx}/${uiStoreKey}`
  }

  // get old version + value from store
  let {value, version, initialValue} = useSelector(root => root.ui.byId[uiStoreKey]) || {}

  // normalize "raw" situation (indicated by defaults.__raw__) into dictionary form
  value = useMemo(() => (has(defaults, '__raw__') ? {__raw__: value} : value), [value])

  // SSR support
  useEffectSSR(() => {
    let {__needCreate__, __version__, ...ui} = versionCheck(
      value,
      version,
      defaults,
      uiStoreKey,
      true
    )
    if (__needCreate__ && !CREATION_TRACKER[uiStoreKey]) {
      // we do want to do this multiple times for a uiStoreKey
      CREATION_TRACKER[uiStoreKey] = true
      // usePersistentShallow sends no __raw__ value but should also store a initialValue
      let initialValue = has(defaults, '__raw__')
        ? defaults.__raw__
        : dict(filterItems(defaults, k => k != '__version__'))
      log.info('create %o [default: %o]', uiStoreKey, initialValue)
      dispatch(uiSetValue(uiStoreKey, ui, false, __version__, initialValue))
    }
  }, [value])

  let ui = versionCheck(value, version, defaults, uiStoreKey, false)
  let clean = ({__needCreate__, __version__, ...ui}) => ui
  return useMemo(
    () => ({
      ui: clean(ui),
      setUI: vals => dispatch(uiSetValue(uiStoreKey, vals)),
    }),
    [ui, dispatch, uiStoreKey]
  )
}

const SETTINGS_DEFAULTS = {
  // name: [version, default]
  colorizeDomains: [1, false],
  headerStyle: [1, 'default'], // default, compact
  navBarTarget: [2, 'centerLeftPortal'],
}

export const useUserSetting = name => {
  let [ver, def] = SETTINGS_DEFAULTS[name] ?? [0, undefined]
  if (def === undefined) log.warn('undefined user setting: %o', name)
  return usePersistent(`Global/${name}`, def, ver)
}
